import { z } from 'zod';

export const commonNumberInputMin = 0;
export const commonNumberInputMax = 1000;

export const commonIdNumberInputMax = 2_147_483_647; // Maximum positive 32-bit signed integer

const mustBeANumberTransformed = (val: string, ctx: z.RefinementCtx): string => {
  // Note: the string "1a" would be parsed as a number by JavaScript's native parseInt(val, 10),
  // whereas z.coerce.number() will generate an error in this scenario. Therefore, we're using Zod's
  // parsing to catch this edge case.
  const toNumber = z.coerce.number();
  const parsed = toNumber.safeParse(val);
  if (!parsed.success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Must be a number'
    });

    // This is a special symbol you can use to return early from the transform function.
    // It has type `never` so it does not affect the inferred return type.
    return z.NEVER;
  }
  return val;
};

/**
 * A schema that can be used with number inputs that are of type string to check that the value
 * is a number.
 */
const stringMustBeANumberSchema = z
  .string()
  .transform((val, ctx) => mustBeANumberTransformed(val, ctx));

/**
 * A schema that can be used with number inputs that are of type string to check that the value
 * is either a number or a blank string.
 */
const stringMustBeANumberOrBlankSchema = z.string().transform((val, ctx) => {
  if (val === '') return val;
  return mustBeANumberTransformed(val, ctx);
});

/**
 * A schema that checks that the input string isn't negative zero (an edge case), since -0 can be
 * later silently coerced into 0.
 */
const stringMustNotBeNegativeZeroSchema = z.string().refine((val) => val !== '-0', {
  message: 'Negative zero is not a valid number.'
});

/**
 * A schema that checks that the input string isn't "0". Since Zod converts "" to 0 when running
 * z.coerce.number(), this should be done before any numeric checks in the pipeline if blank strings
 * are considered to be valid but 0 is considered to be invalid.
 */
const stringMustNotBeZeroSchema = z.string().refine((val) => val !== '0', {
  message: 'Must not be zero.'
});

/**
 * A schema that checks that the input string does not contain a "." since Zod's z.number().int
 * considers "1.0" to be a whole number and doesn't have a seperate validation to check if it is
 * float. Useful for ID numbers, where a decimal point is invalid.
 */
const stringMustNotContainADecimalPointSchema = z.string().refine((val) => !val.includes('.'), {
  message: 'Must not contain a decimal point'
});

/**
 * A schema for forcing a zero string to become a blank string. Useful after performing numeric
 * validations and after converting the value back into a string. Helps prevent the value from
 * becoming 0 unintentionally.
 */
const stringMustTreatZeroAsBlankSchema = z.string().transform((val) => {
  if (val === '0') return '';
  return val;
});

/**
 * A schema that validates that a number is non-negative.
 */
const nonNegativeNumberSchema = z
  .number()
  .nonnegative({ message: 'Must be a non-negative number' });

/**
 * A schema that validates that a number is an integer.
 */
const integerSchema = z.number().int({ message: 'Must be a whole number' });

/**
 * A schema that checks that a number is within a common range.
 */
const commonNumberRangeSchema = z.number().gte(commonNumberInputMin).lte(commonNumberInputMax);

/**
 * A schema that checks that an ID number is less than a common ID number input maximum.
 */
const commonIdNumberInputMaxSchema = z.number().lte(commonIdNumberInputMax);

/**
 * A schema that combines the above validations, providing a common schema that can be used with
 * number inputs. It validates a number-like string, then validates it as a number, before
 * coercing it back into a string for further freeform data entry.
 */
export const commonNumberInputSchema = stringMustBeANumberSchema
  .pipe(stringMustNotBeNegativeZeroSchema)
  .pipe(z.coerce.number())
  .pipe(integerSchema)
  .pipe(commonNumberRangeSchema)
  .pipe(z.coerce.string());

/**
 * A schema for fields that can be blank or a positive number, such as a filterable database ID.
 * These numbers should not contain non-numeric characters or be zero/negative numbers.
 */
export const commonIdNumberInputSchema = stringMustBeANumberOrBlankSchema
  .pipe(stringMustNotBeZeroSchema)
  .pipe(stringMustNotBeNegativeZeroSchema)
  .pipe(stringMustNotContainADecimalPointSchema)
  .pipe(z.coerce.number())
  .pipe(nonNegativeNumberSchema) // Don't check 0 here, since we check this as a string earlier.
  .pipe(integerSchema)
  .pipe(commonIdNumberInputMaxSchema) // Minimum is handled earlier in the pipeline.
  .pipe(z.coerce.string())
  .pipe(stringMustTreatZeroAsBlankSchema);
